import AddIcon from "@mui/icons-material/Add";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Popover } from "@mui/material";
import classNames from "classnames";
import {
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  useRef,
  useState,
} from "react";
import PiMenuButton from "../menu/PiMenuButton";
import type { OnboardingTargetId } from "../onboarding/onboardingTargets";

export function EditorColumnHeader(props: {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  testId?: string;
}) {
  const { icon, title, subtitle, action, testId } = props;

  return (
    <div
      className="h-[42px] min-h-[42px] flex items-center gap-2 px-3 bg-creator-primary-background border-b border-creator-border"
      data-testid={testId}
    >
      {icon && (
        <span className="inline-flex items-center justify-center flex-shrink-0 text-[17px] text-creator-text-sub">
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1 flex flex-col">
        <span className="truncate text-[12px] font-medium uppercase tracking-wide text-creator-text-sub leading-4">
          {title}
        </span>
        {subtitle && (
          <span className="truncate text-[11px] font-normal text-creator-text-sub leading-4 normal-case">
            {subtitle}
          </span>
        )}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

export function EditorColumnFooterButton(props: {
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  testId?: string;
  id?: string;
  onboardingId?: OnboardingTargetId;
}) {
  const { label, icon, disabled, onClick, testId, id, onboardingId } = props;

  return (
    <button
      type="button"
      className="w-full h-[34px] inline-flex items-center justify-center gap-1.5 rounded-5 border-0 bg-creator-primary hover:bg-creator-primary-hover text-white text-[13px] font-semibold cursor-pointer disabled:bg-creator-primary-disabled disabled:cursor-not-allowed"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testId}
      id={id}
      data-onboarding-id={onboardingId}
    >
      {icon ?? <AddIcon fontSize="inherit" />}
      {label}
    </button>
  );
}

export interface EditorColumnFooterMenuOption {
  label: string;
  /** Second line. Use it to say what the option does that its label cannot. */
  description?: string;
  icon?: ReactNode;
  onClick: () => void;
  id?: string;
}

/**
 * A footer button with a second, quieter way to add something.
 *
 * The main segment does exactly what `EditorColumnFooterButton` would; the
 * chevron opens a menu listing every route, the default one included, because
 * the menu is usually the only place the difference between them is spelled
 * out.
 *
 * Deliberately not `PiSplitButton`: that one is a mode selector — it owns a
 * `selectedIndex` and its menu changes what the main button does next time.
 * Here the main action never changes.
 */
export function EditorColumnFooterSplitButton(props: {
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  options: EditorColumnFooterMenuOption[];
  testId?: string;
  menuTestId?: string;
  dropdownLabel?: string;
  id?: string;
  onboardingId?: OnboardingTargetId;
}) {
  const {
    label,
    icon,
    disabled,
    onClick,
    options,
    testId,
    menuTestId,
    dropdownLabel = "More add options",
    id,
    onboardingId,
  } = props;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const segment =
    "h-full inline-flex items-center justify-center gap-1.5 border-0 bg-creator-primary hover:bg-creator-primary-hover text-white cursor-pointer disabled:bg-creator-primary-disabled disabled:cursor-not-allowed";

  return (
    <>
      <div
        ref={anchorRef}
        className="w-full h-[34px] flex items-stretch rounded-5 overflow-hidden"
        data-onboarding-id={onboardingId}
      >
        <button
          type="button"
          className={classNames(segment, "flex-1 text-[13px] font-semibold")}
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          data-testid={testId}
          id={id}
        >
          {icon ?? <AddIcon fontSize="inherit" />}
          {label}
        </button>
        {/* Reads as one control, so the split is a hairline, not a gap. */}
        <span className="w-px bg-white/30 flex-shrink-0" aria-hidden="true" />
        <button
          type="button"
          className={classNames(segment, "w-[30px] flex-shrink-0 text-[18px]")}
          onClick={disabled ? undefined : () => setOpen((prev) => !prev)}
          disabled={disabled}
          aria-label={dropdownLabel}
          aria-haspopup="menu"
          aria-expanded={open ? true : undefined}
          data-testid={menuTestId}
        >
          <KeyboardArrowDownIcon fontSize="inherit" />
        </button>
      </div>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
        // The footer sits at the bottom of the column, so the menu has to grow
        // upward out of the button rather than down off the panel.
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          style: {
            borderRadius: "8px",
            // The shadow alone separates the menu from the page in light mode;
            // in dark a black shadow on a dark ground does nothing, so the
            // border is what gives it an edge there.
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
            border: "1px solid var(--pi-tooltip-border)",
            padding: "6px",
            backgroundColor: "var(--pi-surface)",
            // Matched to the whole group: anchoring to the 30px chevron would
            // hang the menu off the column's right edge.
            width: anchorRef.current?.offsetWidth,
          },
        }}
      >
        <div className="w-full flex flex-col" role="menu">
          {options.map((option) => (
            <PiMenuButton
              key={option.label}
              id={option.id}
              text={option.label}
              description={option.description}
              icon={option.icon}
              variant="modern"
              onClick={() => {
                setOpen(false);
                option.onClick();
              }}
            />
          ))}
        </div>
      </Popover>
    </>
  );
}

export function EditorColumnInlineAddButton(props: {
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  testId?: string;
  id?: string;
  onboardingId?: OnboardingTargetId;
}) {
  const { label, icon, disabled, onClick, testId, id, onboardingId } = props;

  return (
    <button
      type="button"
      className={classNames(
        "w-full h-[30px] inline-flex items-center justify-center gap-1.5 rounded-5",
        "text-creator-primary hover:bg-creator-primary-washed text-[12px] font-semibold cursor-pointer",
        "disabled:text-creator-primary-disabled disabled:cursor-not-allowed disabled:hover:bg-transparent"
      )}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testId}
      id={id}
      data-onboarding-id={onboardingId}
    >
      {icon ?? <AddIcon fontSize="inherit" />}
      {label}
    </button>
  );
}

export function EditorColumnSection(props: {
  title: string;
  icon?: ReactNode;
  defaultExpanded?: boolean;
  renderHeader?: () => ReactNode;
  headerHeightClass?: string;
  /**
   * A control on the right of the header row, rendered as a sibling of the
   * expand/collapse button rather than inside it — a nested <button> is invalid
   * markup, and clicking it would toggle the section.
   *
   * Receives `expand` so an action that reveals content can open the section
   * first; a section collapsed around freshly-swapped content reads as a no-op.
   */
  renderAction?: (controls: { expand: () => void }) => ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  const {
    title,
    icon,
    defaultExpanded = false,
    renderHeader,
    headerHeightClass,
    renderAction,
    children,
    testId,
  } = props;
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div
      className="rounded-5 border border-creator-border bg-creator-surface overflow-hidden"
      data-testid={testId}
    >
      <div
        className={classNames(
          "w-full min-w-0 flex items-center",
          headerHeightClass ?? "h-9"
        )}
      >
        <button
          type="button"
          className={classNames(
            "min-w-0 flex-1 h-full flex items-center gap-2 px-2 bg-transparent border-0 cursor-pointer text-left hover:bg-creator-childHover transition-colors"
          )}
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          <ChevronRightOutlinedIcon
            className={classNames(
              "text-creator-text-sub flex-shrink-0 text-[19px] transition-transform duration-150",
              expanded && "rotate-90"
            )}
            fontSize="inherit"
          />
          {renderHeader ? (
            renderHeader()
          ) : (
            <>
              {icon && (
                <span className="inline-flex items-center justify-center flex-shrink-0 text-[18px] text-creator-text-sub">
                  {icon}
                </span>
              )}
              <span className="min-w-0 flex-1 truncate text-[12px] font-semibold uppercase tracking-wide text-creator-text-sub">
                {title}
              </span>
            </>
          )}
        </button>
        {renderAction && (
          <div className="flex-shrink-0 pr-1.5 flex items-center">
            {renderAction({ expand: () => setExpanded(true) })}
          </div>
        )}
      </div>
      {expanded && <div className="p-1 space-y-px">{children}</div>}
    </div>
  );
}

export function EditorColumnFieldGroup(props: {
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      className={classNames("flex flex-col gap-1 px-1 py-1.5", props.className)}
      data-testid={props.testId}
    >
      {props.children}
    </div>
  );
}

export function EditorColumnEmptyState(props: {
  icon?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div
      className="h-full min-h-[160px] flex flex-col items-center justify-center gap-2 px-4 text-center text-creator-text-sub"
      data-testid={props.testId}
    >
      {props.icon && (
        <span className="inline-flex text-[28px]">{props.icon}</span>
      )}
      <span className="text-[13px] font-normal">{props.children}</span>
    </div>
  );
}

export function EditorColumnRow(props: {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  selected?: boolean;
  softSelected?: boolean; // multi-selection member that is not the primary row
  dropTarget?: boolean;
  disabled?: boolean;
  ghost?: boolean;
  defaultBorder?: boolean;
  variant?: "card" | "flat";
  onClick?: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  role?: string;
  tabIndex?: number;
  dragAttributes?: any;
  dragListeners?: any;
  testId?: string;
  dataAttributes?: Record<string, string | undefined>;
  ariaLabel?: string;
  className?: string;
  titleClassName?: string;
  children?: ReactNode;
}) {
  const {
    icon,
    title,
    subtitle,
    leading,
    trailing,
    selected,
    softSelected,
    dropTarget,
    disabled,
    ghost,
    defaultBorder,
    variant = "card",
    onClick,
    onKeyDown,
    role,
    tabIndex,
    dragAttributes,
    dragListeners,
    testId,
    dataAttributes,
    ariaLabel,
    className,
    titleClassName,
    children,
  } = props;
  const {
    role: dragRole = "button",
    tabIndex: dragTabIndex = 0,
    ...sortableAttributes
  } = dragAttributes ?? {};

  return (
    <div
      role={role ?? (dragRole as string)}
      tabIndex={disabled ? undefined : tabIndex ?? (dragTabIndex as number)}
      data-testid={testId}
      data-selected={selected ? "true" : undefined}
      data-soft-selected={softSelected ? "true" : undefined}
      aria-selected={selected || softSelected ? true : undefined}
      aria-label={ariaLabel}
      {...dataAttributes}
      className={classNames(
        "w-full min-w-0 flex items-center gap-2 rounded-5 text-left box-border select-none transition-colors",
        variant === "card" && [
          "border-[1.75px] border-solid px-2 py-1.5 min-h-[48px]",
          selected
            ? "border-creator-primary bg-creator-primary-washed"
            : softSelected
            ? "border-transparent bg-creator-primary-washed"
            : dropTarget
            ? "border-creator-primary bg-creator-surface"
            : classNames(
                defaultBorder ? "border-creator-border" : "border-transparent",
                "bg-creator-surface hover:bg-creator-childHover"
              ),
        ],
        variant === "flat" && [
          "h-[36px] min-h-[36px] px-2 py-1.5 border-[1.75px] border-solid",
          selected
            ? "border-creator-primary bg-creator-primary-washed"
            : softSelected
            ? "border-transparent bg-creator-primary-washed"
            : "border-transparent hover:bg-creator-childHover",
        ],
        disabled ? "cursor-not-allowed opacity-55" : "cursor-pointer",
        ghost && "opacity-80",
        className
      )}
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled ? undefined : onKeyDown}
      {...sortableAttributes}
      {...dragListeners}
    >
      {leading}
      {icon && (
        <span
          className={classNames(
            "inline-flex items-center justify-center flex-shrink-0 text-[18px]",
            variant === "flat"
              ? "text-creator-primary"
              : selected
              ? "text-creator-primary"
              : "text-creator-text-sub"
          )}
        >
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 flex flex-col">
        <span
          className={classNames(
            "truncate text-[13px] leading-5",
            titleClassName ?? "font-medium",
            selected
              ? "text-creator-primary"
              : variant === "flat"
              ? "text-creator-text-sub"
              : "text-creator-text",
          )}
        >
          {title}
        </span>
        {subtitle && (
          <span className="truncate text-[11px] font-normal text-creator-text-sub leading-4">
            {subtitle}
          </span>
        )}
      </span>
      {children}
      {trailing && (
        <span className="min-w-0 inline-flex items-center justify-end gap-1 flex-shrink-0">
          {trailing}
        </span>
      )}
    </div>
  );
}

export default function EditorColumn(props: {
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  widthClassName?: string;
  bodyClassName?: string;
  className?: string;
  borderSide?: "right" | "left" | "none";
  testId?: string;
  onboardingId?: OnboardingTargetId;
}) {
  const {
    header,
    children,
    footer,
    widthClassName = "w-[250px] min-w-[250px]",
    bodyClassName,
    className,
    borderSide = "right",
    testId,
    onboardingId,
  } = props;

  return (
    <section
      className={classNames(
        widthClassName,
        "h-full min-h-0 flex flex-col bg-creator-surface border-creator-border text-creator-text",
        borderSide === "right" && "border-r",
        borderSide === "left" && "border-l",
        className
      )}
      data-testid={testId}
      data-onboarding-id={onboardingId}
    >
      {header}
      <div
        className={classNames(
          "min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-2.5 space-y-1.5 pit-creator-scrollbar",
          bodyClassName
        )}
      >
        {children}
      </div>
      {footer && (
        <div className="flex-shrink-0 p-2.5 border-t border-creator-border">
          {footer}
        </div>
      )}
    </section>
  );
}
